import { Component, DestroyRef, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IzipayService } from '../../services/izipay.service';
import { IzipayBuyer, IzipayResult } from '../../models/izipay.model';
import { IZIPAY_SANDBOX_ORDER } from '../../config/izipay.config';

let nextId = 0;
@Component({
  selector: 'app-izipay-checkout',
  template: `
    <section class="checkout" aria-label="Pago con Izipay">
      <img src="/logos/izipay-logo.png" alt="Izipay" width="56" height="56" />
      <h3>Paga con Izipay</h3>
      <p>Completa tu pago de forma segura con los medios disponibles en la pasarela.</p>
      @if (!orderId()) { <p class="sandbox">Modo de prueba · Pedido {{ sandbox.orderId }} · S/ {{ sandbox.amount.toFixed(2) }}. No confirma tu compra.</p> }
      @if (testPaid()) { <p role="status">Pago de prueba confirmado. Tu carrito se conserva.</p> }
      @if (error()) { <p class="error" role="alert">{{ error() }}</p> }
      @if (!started() && !testPaid()) {
        <button type="button" (click)="pay()">{{ orderId() ? 'Continuar a pagar' : 'Probar Izipay' }} S/ {{ (orderId() ? amount() : sandbox.amount).toFixed(2) }}</button>
      } @else if (!testPaid()) { <p role="status">Completa el pago en Izipay. Espera la confirmación antes de volver a pagar.</p> }
      <div [id]="containerId" class="izipay-container" [class.active]="started() && !testPaid()"></div>
    </section>`,
  styles: `
    .checkout { padding: 20px; text-align: center; font-family: var(--font-body); }
    img { object-fit: contain; margin: 0 auto 12px; }
    h3 { color: #1a1c48; margin: 0 0 12px; }
    p { font-size: 14px; color: #526980; line-height: 1.6; }
    button { border: 0; border-radius: 8px; padding: 14px 24px; background: #d9002c; color: white; cursor: pointer; }
    .error { color: #b40020; }
    .sandbox { padding: 10px; background: #fff6dc; border-radius: 8px; color: #765312; }
    .izipay-container.active { min-height: 560px; width: 100%; margin-top: 16px; }
  `,
})
export class IzipayCheckoutComponent {
  private readonly izipay = inject(IzipayService);
  private readonly destroyRef = inject(DestroyRef);
  readonly orderId = input<string | null>(null);
  readonly amount = input.required<number>();
  readonly buyer = input<Partial<IzipayBuyer>>();
  readonly paid = output<IzipayResult>();
  // true desde que se abre el formulario hasta que el pago falla; en éxito queda en true (el
  // padre cierra la compra). Le permite al padre bloquear cierres y expiraciones mientras tanto.
  readonly processing = output<boolean>();
  readonly started = signal(false);
  readonly testPaid = signal(false);
  readonly sandbox = IZIPAY_SANDBOX_ORDER;
  readonly error = signal('');
  readonly containerId = `izipay-checkout-${++nextId}`;

  pay(): void {
    if (this.started() || this.testPaid()) return;
    this.started.set(true);
    this.error.set('');
    this.processing.emit(true);
    const orderId = this.orderId();
    this.izipay.pay(orderId ?? this.sandbox.orderId, orderId ? this.amount() : this.sandbox.amount, `#${this.containerId}`, this.buyer())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => {
          if (orderId) this.paid.emit(result);
          else this.testPaid.set(true);
        },
        error: error => {
          this.started.set(false);
          this.processing.emit(false);
          this.error.set(error instanceof Error ? error.message : 'No pudimos confirmar el pago. Revisa tu pedido antes de reintentar.');
        },
      });
  }
}
